import React, { useRef, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { type Media, mediaUrlExtension } from '../api/media';
import { RootState } from '../rootReducer';
import { isLocalUrl, resolvePreviewUrl } from '../service/previewUrl';

interface PublicationImageWithLogoProps {
    imageUrl: string;
    alt?: string;
    className?: string;
    isGlobal?: boolean;
    showLogo?: boolean;
    extension?: string;
    medias?: Media[];
    videoAsset?: string;
}

type PreviewMedia = {
    id: number;
    url: string;
    extension: string;
};

const EMPTY_MEDIAS: Media[] = [];

function previewExtensionForUrl(mediaUrl: string, fallbackExtension: string): string {
    if (isLocalUrl(mediaUrl)) {
        return fallbackExtension || mediaUrlExtension(mediaUrl);
    }

    return mediaUrlExtension(mediaUrl);
}

/**
 * Component to display a publication image with user logo overlay using CSS
 *
 * The logo is displayed only if:
 * - The publication is global (created by ROLE_ADMIN)
 * - The user is an SSO user
 * - The user has enabled displayLogoOnPublications option
 * - A logo is available
 */
const PublicationImageWithLogo: React.FC<PublicationImageWithLogoProps> = ({
    imageUrl,
    alt = 'illustration',
    className = '',
    isGlobal = false,
    showLogo = true,
    extension = '',
    medias = EMPTY_MEDIAS,
    videoAsset,
}) => {
    const isSsoUser = useSelector((state: RootState) => state.user.isSsoUser);
    const userDisplayLogo = useSelector((state: RootState) => state.user.displayLogoOnPublications);
    const userGroupement = useSelector((state: RootState) => state.user.groupement);
    const logoUrl = useSelector((state: RootState) => state.user.logoUrl);

    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const logoRef = useRef<HTMLImageElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const [realImgDimensions, setRealImgDimensions] = useState({ width: 0, height: 0 });
    const [logoOrientation, setLogoOrientation] = useState<'landscape' | 'portrait' | 'square'>(
        'square',
    );
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const previewMedias = useMemo<PreviewMedia[]>(() => {
        if (medias.length === 0) {
            return [
                {
                    id: 0,
                    url: resolvePreviewUrl(imageUrl, { extension, videoAsset }),
                    extension,
                },
            ];
        }

        return [...medias]
            .sort((a, b) => a.priority - b.priority)
            .filter(media => media.url.trim() !== '')
            .map(media => {
                const mediaExtension = previewExtensionForUrl(media.url, extension);

                return {
                    id: media.id,
                    url: resolvePreviewUrl(media.url, {
                        extension: mediaExtension,
                        videoAsset,
                    }),
                    extension: mediaExtension,
                };
            });
    }, [extension, imageUrl, medias, videoAsset]);
    const previewMediaKey = previewMedias.map(media => media.id).join('|');
    const currentMedia = previewMedias[currentMediaIndex] ?? {
        id: 0,
        url: resolvePreviewUrl(imageUrl, { extension, videoAsset }),
        extension,
    };
    const hasMultipleMedias = previewMedias.length > 1;

    const hasSpecificGroupement = userGroupement && userGroupement.id !== 1;
    const displayLogoOnPublications = hasSpecificGroupement
        ? userGroupement?.displayLogoOnPublications || false
        : userDisplayLogo;

    const isVideo = currentMedia.extension.toLowerCase() === 'mp4';

    const shouldDisplayLogo =
        showLogo && isGlobal && isSsoUser && displayLogoOnPublications && logoUrl && !isVideo;

    useEffect(() => {
        if (logoUrl && shouldDisplayLogo) {
            const img = new Image();
            img.onload = () => {
                const ratio = img.width / img.height;
                if (ratio > 1.2) {
                    setLogoOrientation('landscape');
                } else if (ratio < 0.8) {
                    setLogoOrientation('portrait');
                } else {
                    setLogoOrientation('square');
                }
            };
            img.src = logoUrl;
        }
    }, [logoUrl, shouldDisplayLogo]);

    useEffect(() => {
        if (imgRef.current && containerRef.current && shouldDisplayLogo) {
            const currentImgRef = imgRef.current;
            const currentContainerRef = containerRef.current;

            const updateDimensions = () => {
                if (currentImgRef && currentContainerRef) {
                    const containerWidth = currentContainerRef.clientWidth;
                    const containerHeight = currentContainerRef.clientHeight;

                    const naturalWidth = currentImgRef.naturalWidth;
                    const naturalHeight = currentImgRef.naturalHeight;

                    const containerRatio = containerWidth / containerHeight;
                    const imageRatio = naturalWidth / naturalHeight;

                    let displayedWidth, displayedHeight;

                    if (imageRatio > containerRatio) {
                        displayedWidth = containerWidth;
                        displayedHeight = containerWidth / imageRatio;
                    } else {
                        displayedHeight = containerHeight;
                        displayedWidth = containerHeight * imageRatio;
                    }

                    setContainerDimensions({
                        width: containerWidth,
                        height: containerHeight,
                    });

                    setRealImgDimensions({
                        width: displayedWidth,
                        height: displayedHeight,
                    });
                }
            };

            if (currentImgRef.complete && currentImgRef.naturalWidth > 0) {
                updateDimensions();
            } else {
                currentImgRef.addEventListener('load', updateDimensions);
            }

            window.addEventListener('resize', updateDimensions);

            return () => {
                window.removeEventListener('resize', updateDimensions);
                currentImgRef.removeEventListener('load', updateDimensions);
            };
        }
    }, [shouldDisplayLogo, currentMedia.url]);

    useEffect(() => {
        setCurrentMediaIndex(0);
    }, [previewMediaKey]);

    const showPreviousMedia = () => {
        setCurrentMediaIndex(index => (index === 0 ? previewMedias.length - 1 : index - 1));
    };

    const showNextMedia = () => {
        setCurrentMediaIndex(index => (index + 1) % previewMedias.length);
    };

    const showMedia = (index: number) => {
        setCurrentMediaIndex(index);
    };

    return (
        <div
            className={className}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <img
                    ref={imgRef}
                    src={currentMedia.url}
                    alt={alt}
                    style={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
                {shouldDisplayLogo &&
                    realImgDimensions.width > 0 &&
                    (() => {
                        const referenceSize = 1080;
                        const scaleFactor =
                            Math.min(realImgDimensions.width, realImgDimensions.height) /
                            referenceSize;
                        const offset = Math.max(5, 55 * scaleFactor);

                        let maxWidth, maxHeight;
                        if (logoOrientation === 'landscape') {
                            maxWidth = 380 * scaleFactor;
                            maxHeight = 105 * scaleFactor;
                        } else {
                            maxWidth = 200 * scaleFactor;
                            maxHeight = 200 * scaleFactor;
                        }

                        const horizontalOffset =
                            (containerDimensions.width - realImgDimensions.width) / 2;
                        const verticalOffset =
                            (containerDimensions.height - realImgDimensions.height) / 2;

                        return (
                            <img
                                ref={logoRef}
                                src={logoUrl}
                                alt="Logo utilisateur"
                                style={{
                                    position: 'absolute',
                                    top: `${verticalOffset + offset}px`,
                                    right: `${horizontalOffset + offset}px`,
                                    maxWidth: `${maxWidth}px`,
                                    maxHeight: `${maxHeight}px`,
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                            />
                        );
                    })()}
            </div>
            {hasMultipleMedias && (
                <>
                    <button
                        type="button"
                        className="absolute left-2 top-1/2 z-[2] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dark-gray shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label="Afficher le média précédent"
                        onClick={showPreviousMedia}
                    >
                        <ChevronLeft aria-hidden={true} size={20} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 z-[2] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dark-gray shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label="Afficher le média suivant"
                        onClick={showNextMedia}
                    >
                        <ChevronRight aria-hidden={true} size={20} strokeWidth={2.5} />
                    </button>
                    <div className="absolute bottom-3 left-1/2 z-[2] flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/80 px-2 py-1 shadow-sm">
                        {previewMedias.map((media, index) => (
                            <button
                                key={media.id}
                                type="button"
                                className={[
                                    'h-2 w-2 rounded-full transition focus:outline-none focus:ring-2 focus:ring-primary',
                                    index === currentMediaIndex
                                        ? 'bg-primary'
                                        : 'bg-gray/60 hover:bg-gray',
                                ].join(' ')}
                                aria-label={`Afficher le média ${index + 1}`}
                                aria-current={index === currentMediaIndex ? 'true' : undefined}
                                onClick={() => showMedia(index)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default PublicationImageWithLogo;
